import type { ReactNode } from 'react';
import { makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';

// Small presentational pieces shared by the pages.
const useStyles = makeStyles({
  steps: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  bars: { display: 'flex', gap: '4px' },
  bar: { width: '22px', height: '4px', ...shorthands.borderRadius('2px'), backgroundColor: tokens.colorNeutralStroke2 },
  barOn: { backgroundColor: tokens.colorCompoundBrandBackground },
  kicker: {
    margin: 0,
    color: tokens.colorBrandForeground1,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    fontSize: '0.85rem',
    fontWeight: tokens.fontWeightSemibold,
  },
  title: { margin: '4px 0 0', fontSize: '1.75rem', lineHeight: 1.2, color: 'var(--tellec-heading)', fontWeight: 700 },
  lead: { margin: 0, fontSize: '1.05rem', color: tokens.colorNeutralForeground1 },
  muted: { margin: 0, fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 },
  iconCircle: {
    width: '48px',
    height: '48px',
    ...shorthands.borderRadius('50%'),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '26px',
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
  },
  ok: { backgroundColor: tokens.colorPaletteGreenBackground2, color: tokens.colorPaletteGreenForeground2 },
  warn: { backgroundColor: tokens.colorPaletteYellowBackground2, color: tokens.colorNeutralForeground1 },
  list: { listStyleType: 'none', margin: 0, padding: 0, display: 'grid', gap: '10px' },
  item: {
    display: 'grid',
    gridTemplateColumns: '28px 1fr',
    gap: '10px',
    alignItems: 'start',
    padding: '10px 12px',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('6px'),
  },
  itemIcon: { fontSize: '22px', color: tokens.colorBrandForeground1, marginTop: '1px' },
  itemTitle: { display: 'block', fontWeight: tokens.fontWeightSemibold, color: 'var(--tellec-heading)' },
  itemText: { fontSize: tokens.fontSizeBase300, color: tokens.colorNeutralForeground3 },
});

export function Steps({ step, of, label }: { step: number; of: number; label?: string }) {
  const s = useStyles();
  return (
    <div className={s.steps}>
      <div className={s.bars} aria-hidden="true">
        {Array.from({ length: of }, (_, i) => (
          <span key={i} className={mergeClasses(s.bar, i < step && s.barOn)} />
        ))}
      </div>
      <span>{label ?? `Step ${step} of ${of}`}</span>
    </div>
  );
}

export function Kicker({ children }: { children: ReactNode }) {
  return <p className={useStyles().kicker}>{children}</p>;
}

export function Title({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <h1 id={id} className={useStyles().title}>
      {children}
    </h1>
  );
}

export function Lead({ children }: { children: ReactNode }) {
  return <p className={useStyles().lead}>{children}</p>;
}

export function Muted({ children }: { children: ReactNode }) {
  return <p className={useStyles().muted}>{children}</p>;
}

export function IconCircle({ tone, children }: { tone?: 'ok' | 'warn'; children: ReactNode }) {
  const s = useStyles();
  return (
    <div className={mergeClasses(s.iconCircle, tone === 'ok' && s.ok, tone === 'warn' && s.warn)} aria-hidden="true">
      {children}
    </div>
  );
}

export interface ListItem {
  icon: ReactNode;
  title: string;
  text: string;
}

export function IconList({ items, label }: { items: ListItem[]; label: string }) {
  const s = useStyles();
  return (
    <ul className={s.list} aria-label={label}>
      {items.map((it) => (
        <li key={it.title} className={s.item}>
          <span className={s.itemIcon} aria-hidden="true">
            {it.icon}
          </span>
          <div>
            <strong className={s.itemTitle}>{it.title}</strong>
            <span className={s.itemText}>{it.text}</span>
          </div>
        </li>
      ))}
    </ul>
  );
}
